"use client";

/**
 * SI-2-01 — `useSession(urn)` (PRD §5 client SDK).
 *
 * Push-based live session attachment. SWR stays the idiom for request/response
 * data (rollover D3) and is **not** used for session streams.
 *
 * Returns `{ state, headSeq, participants, act, connectionState }` plus
 * capabilities / error helpers. Clients render projections; they never merge
 * (S-D4/P7). Optimistic local echo is discarded — state advances only on
 * authoritative `session.action` notifications.
 *
 * Transport: JSON-RPC 2.0 over WebSocket on the same origin (S-D2), path
 * `/api/sessions/ws` (contracts/sessions `sessions.ws`).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { apiCall } from "../api/client";
import { createAgentIntentCheckpointObserver, verificationAgentIntent } from "../api/contracts/verification-agent-intent";
import type {
  SessionActResponse,
  SessionAttachResponse,
  SessionMode,
  SessionParticipant,
} from "../api/contracts/sessions";
import {
  applyActionNotification,
  applyAttachPayload,
  applyCompactedNotification,
  applyPresenceNotification,
  initialSessionSnapshot,
  makeActIdempotencyKey,
  type ConnectionState,
  type SessionClientConfig,
  type SessionClientReduceFn,
  type SessionClientSnapshot,
} from "./client-core";
import {
  createJsonRpcClient,
  defaultSessionWsUrl,
  portFromWebSocket,
  type JsonRpcClient,
  type MessagePortLike,
  WS_OPEN,
} from "./ws-jsonrpc";

export type UseSessionOptions<S = unknown> = {
  /** Current authenticated account/workspace identity; changing it retires the subscription. */
  authorizationScope?: string;
  /** Attach mode. Default `actor`. */
  mode?: SessionMode;
  /**
   * Pure reducer for this session kind. Required to materialize typed state;
   * without it, `state` stays at the attach snapshot (or null).
   */
  reduce?: SessionClientReduceFn<S>;
  /** Pure factory when attach carries a null snapshot. */
  initialState?: () => S;
  /**
   * When false, the hook does not connect (e.g. `urn` not yet known).
   * Default true when `urn` is non-empty.
   */
  enabled?: boolean;
  /** Override WS URL (tests / alternate host). Default same-origin. */
  wsUrl?: string;
  /**
   * Inject a MessagePortLike instead of opening a WebSocket (unit tests).
   * When set, `wsUrl` is ignored.
   */
  portFactory?: () => MessagePortLike;
  /** Max reconnect attempts before `connectionState=error`. Default 8. */
  maxReconnectAttempts?: number;
};

export type UseSessionResult<S = unknown> = {
  state: S | null;
  headSeq: number;
  participants: SessionParticipant[];
  capabilities: string[];
  /** Connection lifecycle. */
  connectionState: ConnectionState;
  error: string | null;
  /** Last contiguous reduced seq (for reconnect afterSeq). */
  lastSeq: number | null;
  /**
   * Send an intent. Server-wins: does not mutate local state; the next
   * `session.action` notification is authoritative.
   */
  act: (
    intent: Record<string, unknown>,
    idempotencyKey?: string,
  ) => Promise<SessionActResponse>;
};

const DEFAULT_MAX_RECONNECT = 8;

function backoffMs(attempt: number): number {
  // 250ms, 500, 1s, 2s, … capped at 8s. Pure given attempt index.
  return Math.min(8000, 250 * 2 ** Math.max(0, attempt));
}

export function useSession<S = unknown>(
  urn: string | null | undefined,
  options: UseSessionOptions<S> = {},
): UseSessionResult<S> {
  const {
    authorizationScope,
    mode = "actor",
    reduce,
    initialState,
    enabled = true,
    wsUrl,
    portFactory,
    maxReconnectAttempts = DEFAULT_MAX_RECONNECT,
  } = options;

  const configRef = useRef<Pick<SessionClientConfig<S>, "reduce" | "initialState">>({
    reduce,
    initialState,
  });
  configRef.current = { reduce, initialState };

  const [snap, setSnap] = useState<SessionClientSnapshot<S>>(() =>
    initialSessionSnapshot<S>(urn ?? "", "idle"),
  );
  const scopeRef = useRef(authorizationScope);
  scopeRef.current = authorizationScope;
  const [intentError, setIntentError] = useState<string | null>(null);
  const intentObserverRef = useRef<ReturnType<typeof createAgentIntentCheckpointObserver> | null>(null);
  useEffect(() => {
    let active = true;
    setIntentError(null);
    intentObserverRef.current = urn && enabled && mode === "actor" ? createAgentIntentCheckpointObserver({
      urn,
      dispatch: body => apiCall(verificationAgentIntent.observe, { body }),
      onError: error => {
        if (active && scopeRef.current === authorizationScope) setIntentError(error instanceof Error ? error.message : "Task verification intake failed");
      },
    }) : null;
    return () => { active = false; intentObserverRef.current = null; };
  }, [urn, enabled, mode, authorizationScope]);

  const clientRef = useRef<JsonRpcClient | null>(null);
  const connectionGenerationRef = useRef(0);
  const portRef = useRef<MessagePortLike | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const lastSeqRef = useRef<number | null>(null);
  const actCounterRef = useRef(0);
  const urnRef = useRef(urn);
  urnRef.current = urn;

  // Keep lastSeq ref in sync for reconnect afterSeq.
  useEffect(() => {
    lastSeqRef.current = snap.lastSeq;
  }, [snap.lastSeq]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current != null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const teardownSocket = useCallback(() => {
    connectionGenerationRef.current++;
    clearReconnectTimer();
    try {
      clientRef.current?.close();
    } catch {
      /* ignore */
    }
    clientRef.current = null;
    portRef.current = null;
  }, [clearReconnectTimer]);

  const handleNotification = useCallback((method: string, params: unknown) => {
    if (disposedRef.current) return;
    const p = params as Record<string, unknown> | undefined;
    if (method === "session.action" && p) void intentObserverRef.current?.(p);

    setSnap((prev) => {
      const cfg = configRef.current;
      if (method === "session.action" && p) {
        const next = applyActionNotification(
          prev,
          {
            seq: p.seq as number,
            action: p.action as { kind: string; payload: unknown },
            actorId: p.actorId as string,
            ts: p.ts as string,
          },
          cfg,
        );
        return next;
      }
      if (method === "session.presence" && p) {
        return applyPresenceNotification(prev, {
          event: p.event as "joined" | "left" | "idle",
          participant: p.participant as SessionParticipant,
        });
      }
      if (method === "session.compacted" && p) {
        return applyCompactedNotification(prev, {
          snapshotFloor: p.snapshotFloor as number,
        });
      }
      return prev;
    });
  }, []);

  const attachOnClient = useCallback(
    async (client: JsonRpcClient, targetUrn: string, afterSeq: number | null, currentConnection: () => boolean) => {
      const result = (await client.request("session.attach", {
        urn: targetUrn,
        mode,
        afterSeq,
      })) as SessionAttachResponse;

      if (!currentConnection()) return;
      setSnap((prev) =>
        applyAttachPayload(prev, result, configRef.current),
      );
      reconnectAttemptRef.current = 0;
    },
    [mode],
  );

  const connect = useCallback(
    (reason: "mount" | "reconnect" | "resync") => {
      if (disposedRef.current) return;
      const targetUrn = urnRef.current;
      if (!targetUrn || !enabled) return;

      teardownSocket();

      setSnap((prev) => ({
        ...(reason === "mount" ? initialSessionSnapshot<S>(targetUrn, "connecting") : prev),
        urn: targetUrn,
        connectionState: reason === "mount" ? "connecting" : "reconnecting",
        error: null,
        pendingResync: null,
      }));

      let port: MessagePortLike;
      try {
        if (portFactory) {
          port = portFactory();
        } else {
          const url =
            wsUrl ??
            defaultSessionWsUrl(
              typeof window !== "undefined" ? window.location : null,
            );
          // DOM WebSocket ctor — cast through unknown so node/DOM lib variance
          // does not fail tsc when excellent pulls this package in (SI-2-02).
          type WsCtor = new (url: string) => {
            send: (data: string) => void;
            close: () => void;
            readyState: number;
            addEventListener?: (
              type: string,
              listener: (ev: { data?: unknown; code?: number; reason?: string }) => void,
            ) => void;
            removeEventListener?: (
              type: string,
              listener: (ev: { data?: unknown; code?: number; reason?: string }) => void,
            ) => void;
            onmessage: ((ev: { data: unknown }) => void) | null;
            onopen: (() => void) | null;
            onerror: (() => void) | null;
            onclose: ((ev: { code?: number; reason?: string }) => void) | null;
          };
          const WS: WsCtor | undefined =
            typeof WebSocket !== "undefined"
              ? (WebSocket as unknown as WsCtor)
              : ((globalThis as unknown as { WebSocket?: WsCtor }).WebSocket);
          if (!WS) {
            setSnap((prev) => ({
              ...prev,
              connectionState: "error",
              error: "WebSocket not available in this environment",
            }));
            return;
          }
          const ws = new WS(url);
          port = portFromWebSocket(ws);
        }
      } catch (e) {
        setSnap((prev) => ({
          ...prev,
          connectionState: "error",
          error: e instanceof Error ? e.message : String(e),
        }));
        return;
      }

      portRef.current = port;
      const connectionGeneration = connectionGenerationRef.current;
      const currentConnection = () => !disposedRef.current &&
        connectionGenerationRef.current === connectionGeneration && urnRef.current === targetUrn &&
        scopeRef.current === authorizationScope &&
        portRef.current === port;
      const client = createJsonRpcClient({
        port,
        onNotification: (method, params) => {
          if (currentConnection()) handleNotification(method, params);
        },
      });
      clientRef.current = client;

      const afterSeq =
        reason === "mount" ? null : (lastSeqRef.current ?? null);

      // Wait until open when using a real WebSocket; injected ports may already be open.
      const startAttach = () => {
        if (!currentConnection()) return;
        void attachOnClient(client, targetUrn, afterSeq, currentConnection).catch((e) => {
          if (!currentConnection()) return;
          setSnap((prev) => ({
            ...prev,
            connectionState: "error",
            error: e instanceof Error ? e.message : String(e),
          }));
          scheduleReconnect();
        });
      };

      if (port.readyState === WS_OPEN) {
        startAttach();
      } else {
        // Poll readyState — MessagePortLike has no onOpen; WebSocket opens async.
        let tries = 0;
        const tick = () => {
          if (!currentConnection()) return;
          if (port.readyState === WS_OPEN) {
            startAttach();
            return;
          }
          if (port.readyState === 2 || port.readyState === 3) {
            // closing / closed before open
            scheduleReconnect();
            return;
          }
          tries += 1;
          if (tries > 200) {
            setSnap((prev) => ({
              ...prev,
              connectionState: "error",
              error: "websocket open timeout",
            }));
            scheduleReconnect();
            return;
          }
          setTimeout(tick, 25);
        };
        setTimeout(tick, 0);
      }

      const prevOnClose = port.onClose;
      port.onClose = (ev) => {
        if (currentConnection()) {
          setSnap((prev) => ({
            ...prev,
            connectionState: "reconnecting",
          }));
          scheduleReconnect();
        }
        prevOnClose?.(ev);
      };

      function scheduleReconnect() {
        if (!currentConnection()) return;
        // Retire this connection before any queued notification or attach
        // continuation can run. The reconnect timer owns a separate generation;
        // an old close must neither revive its client nor replace a newer one.
        const reconnectGeneration = ++connectionGenerationRef.current;
        clientRef.current = null;
        portRef.current = null;
        try { client.close(); } catch { /* already closed */ }
        const currentReconnect = () => !disposedRef.current &&
          connectionGenerationRef.current === reconnectGeneration &&
          urnRef.current === targetUrn && scopeRef.current === authorizationScope &&
          portRef.current === null;
        if (reconnectAttemptRef.current >= maxReconnectAttempts) {
          setSnap((prev) => ({
            ...prev,
            connectionState: "error",
            error: prev.error ?? "reconnect exhausted",
          }));
          return;
        }
        const attempt = reconnectAttemptRef.current;
        reconnectAttemptRef.current = attempt + 1;
        clearReconnectTimer();
        reconnectTimerRef.current = setTimeout(() => {
          if (currentReconnect()) connect("reconnect");
        }, backoffMs(attempt));
      }
    },
    [
      attachOnClient,
      authorizationScope,
      clearReconnectTimer,
      enabled,
      handleNotification,
      maxReconnectAttempts,
      portFactory,
      teardownSocket,
      wsUrl,
    ],
  );

  // Drive pending resync from core (gap / compacted).
  useEffect(() => {
    if (!snap.pendingResync) return;
    if (disposedRef.current) return;
    if (!urn || !enabled) return;
    // Re-attach with the core's prescribed afterSeq (null for full snapshot).
    lastSeqRef.current = snap.pendingResync.afterSeq;
    connect("resync");
  }, [snap.pendingResync, urn, enabled, connect]);

  // Mount / urn change / enabled.
  useEffect(() => {
    disposedRef.current = false;
    if (!urn || !enabled) {
      teardownSocket();
      setSnap(initialSessionSnapshot<S>(urn ?? "", "idle"));
      return;
    }
    reconnectAttemptRef.current = 0;
    lastSeqRef.current = null;
    connect("mount");
    return () => {
      disposedRef.current = true;
      // Best-effort detach; ignore failures (unmount race).
      const c = clientRef.current;
      if (c) {
        void c.request("session.detach", { urn }).catch(() => {});
      }
      teardownSocket();
    };
  }, [urn, enabled, connect, teardownSocket]);

  const act = useCallback(
    async (
      intent: Record<string, unknown>,
      idempotencyKey?: string,
    ): Promise<SessionActResponse> => {
      const targetUrn = urnRef.current;
      if (!targetUrn) {
        throw new Error("useSession: no urn");
      }
      const client = clientRef.current;
      if (!client) {
        throw new Error("useSession: not connected");
      }
      actCounterRef.current += 1;
      const key =
        idempotencyKey && idempotencyKey.length > 0
          ? idempotencyKey
          : makeActIdempotencyKey(
              `${targetUrn}:${actCounterRef.current}:${Math.random().toString(36).slice(2)}`,
            );
      // Server-wins: do NOT touch local state here. Fan-out session.action
      // is the only path that reduces (S-D4).
      const result = (await client.request("session.act", {
        urn: targetUrn,
        intent,
        idempotencyKey: key,
      })) as SessionActResponse;
      return {
        seq: result.seq,
        deduped: Boolean(result.deduped),
      };
    },
    [],
  );

  return {
    state: snap.state,
    headSeq: snap.headSeq,
    participants: snap.participants,
    capabilities: snap.capabilities,
    connectionState: snap.connectionState,
    error: snap.error ?? intentError,
    lastSeq: snap.lastSeq,
    act,
  };
}
